import { Link, createFileRoute } from "@tanstack/react-router";
import { TOOLS } from "@/tools/_registry";

export const Route = createFileRoute("/")({
  component: LandingPage,
});

function LandingPage() {
  return (
    <div className="foundry-container">
      <header className="border-b border-foundry-border py-12 sm:py-20">
        <div className="foundry-eyebrow mb-5">ai.aakashgoel.com</div>
        <h1 className="foundry-heading">
          Products <em>forged</em>
          <br />
          by AI.
        </h1>
        <p className="mt-6 max-w-[480px] text-sm leading-[1.8] text-foundry-text-muted">
          An experiment in pointing AI at the noise of everyday life — the small frictions, the
          offhand "why doesn't this exist" moments — and letting it turn the good ones into tools.
          I steer. AI ships.
        </p>
      </header>

      <section className="border-b border-foundry-border py-12">
        <p className="mb-4 leading-[1.9] text-foundry-text">
          Every day, ideas surface — the tools I wish existed, the workflows I cobble together by
          hand, the{" "}
          <span className="rounded bg-foundry-accent/10 px-1.5 py-px text-foundry-text-bright">
            "why doesn't this exist?"
          </span>{" "}
          moments. Most evaporate before anyone takes a swing at them. This is an attempt to catch
          a few before they do.
        </p>
        <p className="leading-[1.9] text-foundry-text">
          AI agents sift through my notes, journals, and side-project scratchpads. They surface
          ideas weekly. I pick the ones worth building. They build working prototypes. I steer with
          taste, not code. The goal:{" "}
          <span className="rounded bg-foundry-accent/10 px-1.5 py-px text-foundry-text-bright">
            10+ micro-products, shipped fast, see what sticks.
          </span>
        </p>
      </section>

      <section className="border-b border-foundry-border py-12">
        <div className="foundry-section-label mb-6">The Fleet</div>
        {TOOLS.filter((t) => t.status === "live" || t.status === "in_progress").map((t, i) => (
          <Link
            key={t.slug}
            // Registry routes are dynamic strings; TanStack Router's Link wants a literal.
            // Safe to cast: every tool config validates that route === `/foundry/${slug}`.
            to={t.route as "/foundry/race-strategy"}
            className="mb-4 block rounded-xl border border-foundry-border bg-foundry-bg-elevated p-7 no-underline transition-colors hover:border-foundry-accent/40 hover:bg-foundry-accent/5"
          >
            <div className="mb-3 flex items-start justify-between gap-4">
              <div className="font-display text-[22px] leading-tight text-foundry-text-bright">
                {t.name}
              </div>
              <div className="whitespace-nowrap text-[11px] font-medium tracking-wider text-foundry-accent">
                #{String(i + 1).padStart(3, "0")}
              </div>
            </div>
            <p className="mb-3.5 text-[13px] leading-[1.7] text-foundry-text-muted">{t.tagline}</p>
            <div className="flex flex-wrap gap-5">
              <div className="text-[11px] tracking-wide text-foundry-text-dim">
                origin <span className="text-foundry-text-muted">{t.origin}</span>
              </div>
              <div className="text-[11px] tracking-wide text-foundry-text-dim">
                status{" "}
                <span
                  className={t.status === "live" ? "text-foundry-accent" : "text-foundry-warning"}
                >
                  ● {t.status === "live" ? "live" : "in progress"}
                </span>
              </div>
            </div>
          </Link>
        ))}
        <div className="rounded-xl border border-dashed border-foundry-border p-7 text-center text-[13px] text-foundry-text-dim">
          #00{TOOLS.length + 1} queued — next extraction pass pending
        </div>
      </section>

      <section className="border-b border-foundry-border py-12">
        <div className="foundry-section-label mb-6">Build Log</div>
        {[
          {
            date: "May 02",
            text: (
              <>
                <strong>Vite migration.</strong> Static HTML → typed React on Vite + TanStack
                Router. Per-tool scaffold with shared schemas, contracts, and audit framework so
                every future tool inherits the slop-prevention discipline.
              </>
            ),
          },
          {
            date: "May 01",
            text: (
              <>
                <strong>Ship #001 built.</strong> Race Strategy Calculator — 5 Procam events
                catalogued, pacing engine with elevation/weather/altitude adjustments, every
                assumption cited. Mapbox course maps with story-mode playback for 4 of the
                distances.
              </>
            ),
          },
          {
            date: "May 01",
            text: (
              <>
                <strong>First extraction pass.</strong> Sifted through personal notes and
                scratchpads. Surfaced 5 candidate ideas. Picked Race Strategy as Ship #001.
              </>
            ),
          },
        ].map((entry, i) => (
          <div key={i} className="mb-5 flex flex-col gap-1 sm:flex-row sm:gap-4">
            <div className="min-w-[72px] whitespace-nowrap pt-px text-xs text-foundry-text-dim">
              {entry.date}
            </div>
            <div className="text-[13px] leading-[1.7] text-foundry-text-muted">{entry.text}</div>
          </div>
        ))}
      </section>

      <section className="border-b border-foundry-border py-12">
        <div className="foundry-section-label mb-6">How This Works</div>
        {[
          {
            n: "01",
            label: "Mine.",
            text: "AI agents read across personal notes, journals, prompts, and side projects to spot patterns — manual workarounds, repeated friction, ‘why doesn't this exist' moments.",
          },
          {
            n: "02",
            label: "Select.",
            text: "Weekly idea brief surfaces 3–5 candidates. I pick 1–2 based on market fit, buildability, and personal conviction. B2C preferred.",
          },
          {
            n: "03",
            label: "Forge.",
            text: "AI generates PRD → design → working prototype. I steer with 2–3 rounds of feedback. Total time: 2–5 hours of my week.",
          },
          {
            n: "04",
            label: "Ship.",
            text: "Each tool launches at /foundry/{name}. Usage tracked. Winners iterated, losers archived. Volume of shots on goal.",
          },
        ].map((s) => (
          <div key={s.n} className="mb-5 flex items-start gap-4">
            <div className="min-w-[24px] pt-0.5 text-[11px] font-medium text-foundry-accent">{s.n}</div>
            <div className="text-[13px] leading-[1.7] text-foundry-text-muted">
              <strong className="font-medium text-foundry-text">{s.label}</strong> {s.text}
            </div>
          </div>
        ))}
      </section>

      <footer className="flex flex-wrap items-center justify-between gap-4 py-12">
        <div className="text-xs text-foundry-text-dim">Aakash Goel · Bengaluru · 2026</div>
        <div className="flex gap-5">
          <a
            href="https://x.com/aakashgoel"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-foundry-text-dim transition-colors hover:text-foundry-accent"
          >
            X
          </a>
        </div>
      </footer>
    </div>
  );
}
